/**
 * Warms the browser cache for a tray hover preview's image while the open delay
 * (`PHOTO_TRAY_HOVER_OPEN_DELAY_MS` / `PHOTO_TRAY_FOCUS_OPEN_DELAY_MS`) is still running, so the
 * preview paints at once when it opens instead of only starting its download then — the preview's
 * `<img>` doesn't exist until the popover mounts its content.
 *
 * Each URL is requested at most once while it's cached here; skimming the pointer back and forth
 * over the same thumbnails costs nothing after the first pass. The held `Image`s keep the requests
 * (and decoded bitmaps) alive; the oldest is dropped past `MAX_HELD` so a long session doesn't pin
 * every preview it ever touched.
 */

const MAX_HELD = 60

const held = new Map<string, HTMLImageElement>()

export function preloadTrayPreviewImage(url: string | null | undefined): void {
  if (!url || typeof Image === 'undefined') return

  if (held.has(url)) {
    // Refresh recency so a thumbnail the user keeps returning to isn't the one evicted.
    const image = held.get(url) as HTMLImageElement

    held.delete(url)
    held.set(url, image)
    return
  }

  const image = new Image()

  image.decoding = 'async'
  // A failed request shouldn't block a later retry (e.g. after a network blip).
  image.onerror = () => {
    if (held.get(url) === image) held.delete(url)
  }

  image.src = url
  held.set(url, image)

  // Decode off the main thread ahead of time too, so the first paint doesn't pay for it.
  // Rejections (a failed load, or an unsupported environment) are expected and harmless.
  image.decode?.().catch(() => {})

  if (held.size > MAX_HELD) {
    const oldest = held.keys().next().value

    if (oldest !== undefined) held.delete(oldest)
  }
}

/** Test-only: forget every preloaded URL. */
export function resetTrayPreviewImagePreloadsForTests(): void {
  held.clear()
}
